import React, { useState } from 'react';
import { Download } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { format } from 'date-fns';

// Define a flexible interface that can handle any key-value pairs
interface DynamicField {
  label: string;
  value: string;
  category: string;
  width?: 'half' | 'full';
}

interface FormData {
  fields: DynamicField[];
  title?: string;
}

// CSS styles
const styles = {
  container: {
    fontFamily: 'Times New Roman, serif',
    lineHeight: 1.5,
    color: '#000',
    maxWidth: '1000px',
    margin: '0 auto',
    padding: '20px'
  },
  heading: {
    textAlign: 'center' as const,
    fontSize: '18px',
    marginBottom: '20px'
  },
  header: {
    textAlign: 'center' as const,
    marginBottom: '30px'
  },
  formLine: {
    display: 'flex',
    marginBottom: '15px',
    alignItems: 'baseline',
    flexWrap: 'wrap' as const
  },
  formLineContent: {
    flex: 1,
    borderBottom: '1px solid #000',
    margin: '0 30px 0 10px',
    paddingLeft: '5px',
    minHeight: '18px'
  },
  formLabel: {
    fontWeight: 'normal',
    minWidth: '100px'
  },
  formNote: {
    fontSize: '12px',
    fontStyle: 'italic',
    textAlign: 'right' as const
  },
  fixedContent: {
    borderBottom: 'none',
    paddingLeft: 0
  },
  formSection: {
    marginBottom: '25px'
  },
  sectionHeader: {
    fontWeight: 'bold',
    borderBottom: '1px solid #000',
    marginBottom: '15px',
    paddingBottom: '5px'
  },
  fieldContainer: {
    display: 'flex',
    width: '100%'
  },
  fieldContainerHalf: {
    display: 'flex',
    width: '50%'
  },
  officialSection: {
    paddingTop: '70px'
  },
  downloadButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px 15px',
    backgroundColor: '#4a5568',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 'bold',
    marginTop: '20px',
    transition: 'background-color 0.3s'
  },
  downloadIcon: {
    marginRight: '8px'
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '30px'
  }
};

function getDob(Value) {
  if (Value) {
    const petDOB = new Date(Value);
    if (!isNaN(petDOB.getTime()) && petDOB.getTime() !== 0) {
      return format(petDOB, 'MM/dd/yyyy');
    }
  }
  return '';
}
const PetLicenseForm: React.FC<{
  data: FormData;
}> = ({ data }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  // Group fields by category
  const fieldsByCategory: { [key: string]: DynamicField[] } = {};
  data.fields.forEach((field) => {
    if (!fieldsByCategory[field.category]) {
      fieldsByCategory[field.category] = [];
    }
    fieldsByCategory[field.category].push(field);
  });

  // Generate Field Component
  const renderField = (field: DynamicField) => {
    const containerStyle =
      field.width === 'half'
        ? styles.fieldContainerHalf
        : styles.fieldContainer;
    return (
      <div key={field.label} style={containerStyle}>
        <div style={styles.formLabel}>{field.label}:</div>
        <div style={styles.formLineContent}>{field.value || ''}</div>
      </div>
    );
  };

  const downloadPDF = async () => {
    setIsDownloading(true);

    try {
      const formElement = document.getElementById('pet-license-form');
      if (!formElement) {
        console.error('Form element not found');
        return;
      }

      const canvas = await html2canvas(formElement, {
        scale: 2,
        logging: false,
        useCORS: true
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save('pet-license-application.pdf');
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div>
      <div id="pet-license-form" style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.heading}>
            {data.title || 'PET LICENSE APPLICATION'}
          </h1>
        </div>

        {/* Render sections by category */}
        {Object.keys(fieldsByCategory).map((category) => (
          <div key={category} style={styles.formSection}>
            {category !== 'Official' && (
              <div style={styles.sectionHeader}>{category}</div>
            )}

            <div
              style={
                category === 'Official' ? styles.officialSection : undefined
              }
            >
              <div style={styles.formLine}>
                {fieldsByCategory[category].map((field) => renderField(field))}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={styles.buttonContainer}>
        <button
          onClick={downloadPDF}
          disabled={isDownloading}
          style={styles.downloadButton as React.CSSProperties}
        >
          <Download
            size={20}
            style={styles.downloadIcon as React.CSSProperties}
          />
          {isDownloading ? 'Generating PDF...' : 'Download Application'}
        </button>
      </div>
    </div>
  );
};

interface DownloadApplicationProps {
  ApplicationDetails: any;
  title?: string;
}

// Helper function to convert flat object to structured field array
const convertToFieldsArray = (data: any): DynamicField[] => {
  const result: DynamicField[] = [];

  // Define categories and their fields
  const fieldCategories: {
    [key: string]: { category: string; width?: 'half' | 'full' };
  } = {
    // Owner fields
    ApplicantName: { category: 'Owner Information', width: 'half' },
    OwnerName: { category: 'Owner Information', width: 'half' },
    OwnerEmailAddress: { category: 'Owner Information', width: 'half' },
    OwnerContactNumber: { category: 'Owner Information', width: 'half' },
    OwnerAddress: { category: 'Owner Information', width: 'full' },

    // Tree Removal Information
    FullName: { category: 'Tree Removal Information', width: 'half' },
    BlockLot: { category: 'Tree Removal Information', width: 'half' },
    AddressWhereTreeAreLocated: {
      category: 'Tree Removal Information',
      width: 'full'
    },
    NearestCrossStreet: { category: 'Tree Removal Information', width: 'half' },
    phoneNumber: { category: 'Tree Removal Information', width: 'half' },
    NumberOfTreesToBeRemoved: {
      category: 'Tree Removal Information',
      width: 'half'
    },
    PurposeOfRemoval: { category: 'Tree Removal Information', width: 'full' },
    'Lot Size (S.F./ Acreage)': {
      category: 'Tree Removal Information',
      width: 'half'
    },
    'Are trees being removed from a restricted area? (i.e. Wetlands, Easements, Buffers, etc.)':
      { category: 'Tree Removal Information', width: 'full' },

    // Pet fields
    PetType: { category: 'Pet Information', width: 'half' },
    PetName: { category: 'Pet Information', width: 'half' },
    PetBreed: { category: 'Pet Information', width: 'half' },
    PetDateOfBirth: { category: 'Pet Information', width: 'half' },
    PetWeight: { category: 'Pet Information', width: 'half' },
    PetHair: { category: 'Pet Information', width: 'half' },
    PetColor: { category: 'Pet Information', width: 'half' },
    PetSex: { category: 'Pet Information', width: 'half' },
    IsSpayedNeutered: { category: 'Pet Information', width: 'half' },
    ChipNumber: { category: 'Pet Information', width: 'half' },

    // Vet fields
    RabiesIssueDate: { category: 'Veterinary Information', width: 'half' },
    RabiesExpirationDate: { category: 'Veterinary Information', width: 'half' },
    VetClinic: { category: 'Veterinary Information', width: 'half' },
    VetPhoneNumber: { category: 'Veterinary Information', width: 'half' }
  };

  // Format IsSpayedNeutered (boolean/1/0/yes/no) to "Yes" or "No"
  const formatSpayedNeutered = (val: unknown): string => {
    if (val === true || val === 1 || val === '1' || val === 'Yes' || val === 'yes') return 'Yes';
    if (val === false || val === 0 || val === '0' || val === 'No' || val === 'no') return 'No';
    return val ? 'Yes' : 'No';
  };

  // Process each key in the input data
  Object.keys(data).forEach((key) => {
    // Format label with spaces (e.g., "PetName" -> "Pet Name")
    const label = key.replace(/([A-Z])/g, ' $1').trim();

    // Get category info or default to "Other Information"
    const categoryInfo = fieldCategories[key] || {
      category: 'Other Information',
      width: 'full'
    };
    const rawValue = data[key];
    const value =
      key === 'IsSpayedNeutered'
        ? formatSpayedNeutered(rawValue)
        : key.includes('Date')
          ? getDob(rawValue)
          : rawValue ?? '';
    result.push({
      label,
      value: String(value),
      category: categoryInfo.category,
      width: categoryInfo.width
    });
  });

  // Add official fields
  ['Received By', 'Signature', 'Date Processed'].forEach((label) => {
    result.push({
      label,
      value: '',
      category: 'Official',
      width: 'half'
    });
  });

  return result;
};

// Main component that uses the dynamic data passed as props
const DownloadApplication: React.FC<DownloadApplicationProps> = ({
  ApplicationDetails,
  title
}) => {
  // Convert flat object to fields array
  const fields = convertToFieldsArray(ApplicationDetails);

  return <PetLicenseForm data={{ fields, title: title }} />;
};

export default DownloadApplication;
